// Driver money — the lifetime totals for the driving half of the person.
//
// Was a collapsed accordion of four label/value pairs. Now a `SectionCard` of
// `Figure`s, the same shape and the same title idiom as Rider money on the tab beside
// it — the two halves of one person's money should not read as two different products.
import { useCallback } from 'react';
import { DollarBadgeIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters } from '../../../hooks';
import { getDriverCashFlow } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import { Figure, Figures, SectionSkeleton, SectionError, useSectionFetch } from '../../../pages/UserProfile/parts';

const DriverCashFlowCard = ({ user }) => {
  const dispatch = useDispatch();
  const f = useFormatters();
  const data = useSelector((state) => state.users.selection.driverCashFlow);

  const load = useCallback(() => getDriverCashFlow(user.id)(dispatch), [user.id, dispatch]);
  const [status, retry] = useSectionFetch(load);

  return (
    <SectionCard
      title="Driver money"
      icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
    >
      {status === 'loading' && <SectionSkeleton />}
      {status === 'failed' && <SectionError onRetry={retry}>Couldn&rsquo;t load these totals.</SectionError>}
      {/* The reducer defaults a missing payload to `{}`, so a driver with no completed
          trips arrives as an object with no fields rather than as null. Zeroes are the
          honest answer there — they have earned nothing, which is a fact, not a gap. */}
      {status === 'done' && !data && <EmptyRows>No driving activity yet.</EmptyRows>}
      {status === 'done' && data && (
        <Figures>
          {/* Earnings first and tinted: it is the number a driver on the phone is asking
              about, and the three counts behind it are what it is made of. */}
          <Figure label="Lifetime earned" value={f.number.currency(data.totalEarnings)} palette="success" />
          <Figure label="Tips" value={f.number.currency(data.totalTips)} />
          <Figure label="Trips" value={f.number.decimal(data.totalTrips)} />
          <Figure label="Rides" value={f.number.decimal(data.totalRides)} />
        </Figures>
      )}
    </SectionCard>
  );
};

export default DriverCashFlowCard;
